export interface Reward {
  id: number | string;
  name: string;
  points: number;
  stock: number;
  cover: string;
}

export type OrderStatus = "pending" | "shipped" | "completed";

export interface RedemptionOrder {
  id: string;
  memberId: string;
  memberName: string;
  memberNo: string;
  rewardId: number | string;
  rewardName: string;
  rewardCover: string;
  pointsCost: number;
  createdAt: string;
  status: OrderStatus;
  trackingNo?: string;
  address?: string;
}

const SEED_ORDERS: RedemptionOrder[] = [];

const _rewards: Reward[] = [];

export function replaceRewardsFromApi(items: Reward[]) {
  _rewards.length = 0;
  _rewards.push(...items);
  notify();
}

export function replaceOrdersFromApi(orders: RedemptionOrder[]) {
  _orders.length = 0;
  _orders.push(...orders.map((o) => ({ ...o })));
  notify();
}

const _orders: RedemptionOrder[] = [...SEED_ORDERS];
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => fn());
}

export const rewardsStore = {
  getAll: (): Reward[] => _rewards.map((r) => ({ ...r })),

  getById: (id: number | string): Reward | undefined => _rewards.find((r) => r.id === id),

  getStock: (id: number | string): number => _rewards.find((r) => r.id === id)?.stock ?? 0,

  decrement: (id: number | string): boolean => {
    const reward = _rewards.find((r) => r.id === id);
    if (!reward || reward.stock <= 0) return false;
    reward.stock -= 1;
    notify();
    return true;
  },

  updateStock: (id: number | string, stock: number) => {
    const reward = _rewards.find((r) => r.id === id);
    if (reward) { reward.stock = Math.max(0, stock); notify(); }
  },

  updatePoints: (id: number | string, points: number) => {
    const reward = _rewards.find((r) => r.id === id);
    if (reward) { reward.points = Math.max(0, points); notify(); }
  },

  updateName: (id: number | string, name: string) => {
    const reward = _rewards.find((r) => r.id === id);
    if (reward) { reward.name = name; notify(); }
  },

  add: (item: Omit<Reward, 'id'>) => {
    _rewards.push({ ...item, id: `r${Date.now()}` });
    notify();
  },

  remove: (id: number | string) => {
    const idx = _rewards.findIndex((r) => r.id === id);
    if (idx > -1) { _rewards.splice(idx, 1); notify(); }
  },

  // Order management
  getOrders: (): RedemptionOrder[] => [..._orders].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),

  getOrdersByMemberId: (memberId: string): RedemptionOrder[] =>
    [..._orders]
      .filter((o) => o.memberId === memberId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),

  addOrder: (order: Omit<RedemptionOrder, 'id' | 'createdAt'>) => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    _orders.unshift({ ...order, id: `ord-${Date.now()}`, createdAt: ts });
    notify();
  },

  updateOrderStatus: (id: string, status: OrderStatus, trackingNo?: string) => {
    const order = _orders.find((o) => o.id === id);
    if (order) {
      order.status = status;
      if (trackingNo) order.trackingNo = trackingNo;
      notify();
    }
  },

  subscribe: (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
